/**
 * MicTestScreen
 *
 * Microphone test screen before starting the voice assessment.
 * Uses AudioContext + AnalyserNode + Canvas for a visual waveform.
 * Shows green checkmark once audio is detected.
 * "Start Call" button enabled after successful mic test.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface MicTestScreenProps {
  onStartCall: () => void;
  onBack: () => void;
}

/** Minimum average amplitude to consider mic as "detected" */
const MIC_DETECTION_THRESHOLD = 0.02;
/** How many consecutive frames above threshold to confirm detection */
const DETECTION_FRAMES_REQUIRED = 5;

export function MicTestScreen({ onStartCall, onBack }: MicTestScreenProps) {
  const [micDetected, setMicDetected] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const detectionCountRef = useRef(0);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  const startMicTest = useCallback(async () => {
    setMicError(null);
    setMicDetected(false);
    detectionCountRef.current = 0;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setIsListening(true);

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const draw = () => {
        animationFrameRef.current = requestAnimationFrame(draw);
        analyser.getByteFrequencyData(dataArray);

        const width = canvas.width;
        const height = canvas.height;
        const dpr = window.devicePixelRatio || 1;

        ctx.clearRect(0, 0, width, height);

        // Calculate average amplitude (0-1)
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i]! / 255;
        }
        const average = sum / bufferLength;

        // Detection logic
        if (average > MIC_DETECTION_THRESHOLD) {
          detectionCountRef.current++;
          if (detectionCountRef.current >= DETECTION_FRAMES_REQUIRED) {
            setMicDetected(true);
          }
        } else {
          // Allow small gaps, only reset if count is low
          if (detectionCountRef.current < DETECTION_FRAMES_REQUIRED) {
            detectionCountRef.current = Math.max(0, detectionCountRef.current - 1);
          }
        }

        // Draw waveform bars
        const barCount = 32;
        const barWidth = (width / dpr) / barCount * 0.7;
        const gap = (width / dpr) / barCount * 0.3;

        for (let i = 0; i < barCount; i++) {
          const dataIndex = Math.floor((i / barCount) * bufferLength);
          const value = (dataArray[dataIndex] ?? 0) / 255;
          const barHeight = Math.max(2 * dpr, value * (height * 0.8));

          const x = (i * (barWidth + gap)) * dpr;
          const y = (height - barHeight) / 2;

          ctx.fillStyle = micDetected
            ? 'var(--emerald-500)'
            : `rgba(14, 165, 233, ${0.4 + value * 0.6})`; // sky-500 with variable opacity
          ctx.beginPath();
          ctx.roundRect(x, y, barWidth * dpr, barHeight, 2 * dpr);
          ctx.fill();
        }
      };

      draw();
    } catch (err) {
      console.error('[MicTestScreen] Mic access error:', err);
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setMicError('Microphone access denied. Please allow microphone access and try again.');
      } else if (err instanceof DOMException && err.name === 'NotFoundError') {
        setMicError('No microphone found. Please connect a microphone and try again.');
      } else {
        setMicError('Unable to access microphone. Please check your settings.');
      }
    }
  }, [micDetected]);

  // Start mic test on mount
  useEffect(() => {
    startMicTest();
    return cleanup;
  }, [startMicTest, cleanup]);

  // Setup canvas DPR
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
  }, []);

  return (
    <div className="learner-mictest">
      <div className="learner-mictest-card">
        <h2 className="learner-mictest-title">Test Your Microphone</h2>
        <p className="learner-mictest-subtitle">
          Say "hello" to make sure your microphone is working.
        </p>

        <div className="learner-mictest-visualizer">
          <canvas
            ref={canvasRef}
            className="learner-mictest-canvas"
          />

          {/* Status indicator */}
          <div className={`learner-mictest-status ${micDetected ? 'detected' : isListening ? 'listening' : ''}`}>
            {micDetected ? (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="12" fill="var(--emerald-500)" />
                  <path d="M7 12.5L10 15.5L17 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Microphone detected</span>
              </>
            ) : isListening ? (
              <>
                <div className="learner-mictest-pulse" />
                <span>Listening...</span>
              </>
            ) : null}
          </div>
        </div>

        {micError && (
          <div className="error-box learner-mictest-error">
            <p>{micError}</p>
            <button className="btn btn-secondary mt-4" onClick={startMicTest}>
              Retry
            </button>
          </div>
        )}

        <div className="learner-mictest-actions">
          <button
            className="btn btn-secondary"
            onClick={onBack}
          >
            Back
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              cleanup();
              onStartCall();
            }}
            disabled={!micDetected}
          >
            Start Call
          </button>
        </div>
      </div>
    </div>
  );
}
